// Double-key lookup of a store's subscription (read alignment only, no data rewrite).
// store_subscriptions rows may carry either the merchant's store_settings.id or a
// stores.id. Readers first look up the resolved real store id (pickRealStore) exactly
// as before; only when nothing is found do they fall back to the OTHER ids owned by
// the authenticated merchant (their own store_settings / stores rows) — never an id
// coming from anyone else.

import { pickRealStore } from "@/lib/merchantStore";

/** Statuses a plan reader considers current (unchanged). */
export const ELIGIBLE_SUBSCRIPTION_STATUSES = ["active", "trial", "canceling"] as const;

/** Name given to the empty store row auto-created at signup (create_store_for_new_user). */
export const AUTO_STORE_NAME = "my-store";

type OwnRow = { id: string; created_at?: string | null; store_name?: string | null };

/**
 * Fallback ids for the subscription lookup: the merchant's own store_settings ids and
 * own stores ids (rows already scoped to the authenticated user), in pickRealStore order
 * (settings oldest first, then stores oldest first), deduplicated, WITHOUT the resolved id.
 * Returns [] when the resolved id is not one of the merchant's own ids, so a foreign
 * store id never pulls the merchant's other subscriptions.
 */
export function subscriptionFallbackIds(
  resolvedId: string | null | undefined,
  ownSettingsRows: OwnRow[] | null | undefined,
  ownStoresRows: OwnRow[] | null | undefined,
  opts: { excludeAutoStore?: boolean } = {},
): string[] {
  if (!resolvedId) return [];
  const byAge = (rows: OwnRow[] | null | undefined) =>
    [...(rows ?? [])].sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")));
  const settings = byAge(ownSettingsRows);
  const stores = byAge(ownStoresRows);
  const own = new Set([...settings, ...stores].map((r) => r.id));
  if (!own.has(resolvedId)) return [];
  const settingsIds = new Set(settings.map((r) => r.id));
  const out: string[] = [];
  for (const r of [...settings, ...stores]) {
    if (r.id === resolvedId || out.includes(r.id)) continue;
    // Optional: skip the empty auto-created "my-store" row (stores only, never a settings id).
    if (opts.excludeAutoStore && !settingsIds.has(r.id) && (r.store_name ?? "").trim() === AUTO_STORE_NAME) continue;
    out.push(r.id);
  }
  return out;
}

/** Ids whose store_subscriptions changes must refresh the displayed plan. */
export function subscriptionWatchIds(resolvedId: string | null | undefined, fallbackIds: string[]): string[] {
  return resolvedId ? [resolvedId, ...fallbackIds.filter((id) => id !== resolvedId)] : [];
}

type SubRow = { store_id: string | null; status: string | null; created_at?: string | null };

/**
 * Picks the fallback subscription deterministically: eligible status only, the first id
 * in `fallbackIds` order wins, newest row first within the same id. Rows on any id not
 * listed are ignored (isolation even if the query returned more).
 */
export function pickFallbackSubscription<T extends SubRow>(
  rows: T[] | null | undefined,
  fallbackIds: string[],
  statuses: readonly string[] = ELIGIBLE_SUBSCRIPTION_STATUSES,
): T | null {
  for (const id of fallbackIds) {
    const hit = (rows ?? [])
      .filter((r) => r.store_id === id && statuses.includes(String(r.status)))
      .sort((a, b) => String(b.created_at ?? "").localeCompare(String(a.created_at ?? "")))[0];
    if (hit) return hit;
  }
  return null;
}

/** Real store + fallback ids from the merchant's own rows (shared by the plan readers). */
export function resolveSubscriptionIds(
  ownSettingsRows: OwnRow[] | null | undefined,
  ownStoresRows: OwnRow[] | null | undefined,
): { storeId: string | null; fallbackIds: string[] } {
  const storeId = pickRealStore(ownSettingsRows, ownStoresRows)?.row.id ?? null;
  return { storeId, fallbackIds: subscriptionFallbackIds(storeId, ownSettingsRows, ownStoresRows) };
}
